import { superstructResolver } from '@hookform/resolvers/superstruct';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonSave } from '@adflow/frontend/ui-buttons';
import {
	InputTextFieldWithControl,
	PasswordInput,
} from '@adflow/frontend/ui-form';
import { LoadingIndicator } from '@adflow/frontend/ui-graphics';
import { su, typography } from '@adflow/frontend/ui-theme';
import { useMutationLogin } from '@adflow/frontend/util-auth';
import { LoginDto } from '@adflow/shared/util-auth';

import { useResetPasswordModalListener } from './ResetPassword';
import { ResetPasswordModal } from './ResetPasswordModal';

const formSchema = LoginDto;
type formValues = LoginDto;

export function LoginForm() {
	const { t } = useTranslation('login');
	const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);

	const { mutateAsync: requestLogin, isPending } = useMutationLogin();

	const formMethods = useForm<formValues>({
		defaultValues: {
			email: '',
			password: '',
		},
		resolver: superstructResolver(formSchema, { coerce: true, mask: true }),
	});

	const handleSubmit = formMethods.handleSubmit(async (data) => {
		await requestLogin(data);
	});

	useResetPasswordModalListener(setShowResetPasswordModal);

	const openResetPasswordModal = () => {
		setShowResetPasswordModal(true);
	};

	const closeResetPasswordModal = () => {
		setShowResetPasswordModal(false);
	};

	return (
		<>
			<FormProvider {...formMethods}>
				<form
					onSubmit={handleSubmit}
					css={{
						display: 'grid',
						gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
						gridTemplateRows: 'repeat(3, minmax(0, 1fr))',
					}}
				>
					<div css={{ gridRow: 'span 1 / span 1' }}>
						<InputTextFieldWithControl
							name="email"
							label={t('form label Email')}
							placeholder={t('form placeholder Email')}
							autoFocus
							type="email"
							forceAutoComplete
							disablePasswordManager={false}
							data-testid="email-input"
							useRandomName={false}
						/>
					</div>
					<div css={{ gridRow: 'span 1 / span 1' }}>
						<PasswordInput
							name="password"
							label={t('form label Password')}
							placeholder={t('form placeholder Password')}
							disablePasswordManager={false}
							forceAutoComplete
							data-testid="password-input"
						/>
					</div>
					<div
						css={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							justifyContent: 'space-between',
							gridRow: 'span 1 / span 1',
						}}
					>
						<button
							css={[
								typography.clickableLink,
								su.textSm,
								{ width: '4rem', textAlign: 'left' },
							]}
							// Default type is submit, which we don't want for forgot password
							type="button"
							onClick={openResetPasswordModal}
						>
							{t('Forgot password')}
						</button>
						<ButtonSave type="submit" disabled={isPending}>
							{isPending ? (
								<LoadingIndicator immediate color="white" />
							) : (
								t('action Log in')
							)}
						</ButtonSave>
					</div>
				</form>
			</FormProvider>
			<ResetPasswordModal
				show={showResetPasswordModal}
				handleClose={closeResetPasswordModal}
				email={formMethods.watch('email')}
			/>
		</>
	);
}
